import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import { SERVICES } from "@/data/index";
import { springPresets } from "@/lib/motion";

/* @section: services-hero */
function ServicesHero() {
  return (
    <section className="py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-2xl"
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block">Services</span>
          <h1
            className="text-4xl md:text-5xl font-semibold text-foreground leading-[1.3] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            サービス一覧
          </h1>
          <div className="w-12 h-px bg-primary mb-6" />
          <p className="text-muted-foreground leading-[1.9] text-lg">
            個人から法人まで、目的と規模に合わせた
            コーチング・研修プログラムをご提供します。
          </p>
        </motion.div>
      </div>
    </section>
  );
}

const SERVICE_IMAGES: Record<string, string> = {
  leadership: "/images/gen/leadership_img_2.png",
  organization: "/images/gen/org_team_img_2.png",
  "ai-business": "/images/gen/ai_biz_img_2.png",
  coaching: "https://agents-download.skywork.ai/image/rt/ffa9a697170e8cf6c63a7d9750c46c61.jpg",
};

/* @section: services-list */
function ServicesList() {
  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="flex flex-col gap-16">
          {SERVICES.map((service, i) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.05 }}
              className="grid md:grid-cols-2 gap-10 items-start pb-16 border-b border-border last:border-0"
            >
              <div>
                <div className="rounded-sm overflow-hidden mb-6" style={{ boxShadow: "0 4px 24px -4px color-mix(in srgb, var(--primary) 12%, transparent)" }}>
                  <img src={SERVICE_IMAGES[service.id]} alt={service.title} className="w-full h-52 object-cover" />
                </div>
                <span className="text-xs text-primary tracking-wider mb-2 block">{service.subtitle}</span>
                <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {service.title}
                </h2>
                <div className="w-10 h-px bg-primary mb-5" />
                <p className="text-muted-foreground leading-[1.9] mb-6">{service.description}</p>
                <div className="text-xs text-muted-foreground/70 bg-muted/40 px-4 py-2 rounded-sm inline-block">
                  対象：{service.target}
                </div>
              </div>
              <div className="bg-muted/30 border border-border rounded-sm p-8">
                <h3 className="text-sm font-semibold text-foreground mb-5 tracking-wider" style={{ letterSpacing: "0.08em" }}>
                  プログラム内容
                </h3>
                <ul className="flex flex-col gap-4">
                  {service.details.map((detail, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle size={15} className="text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground leading-relaxed">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* @section: services-cta */
function ServicesCTA() {
  return (
    <section className="py-20 bg-muted/30 border-t border-border">
      <div className="container mx-auto px-6 text-center">
        <h2
          className="text-2xl md:text-3xl font-semibold text-foreground mb-4"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          ご要望に合わせたプログラム設計も可能です。
        </h2>
        <p className="text-muted-foreground mb-8">
          まずはお気軽にご相談ください。初回相談は無料です。
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm"
          style={{ letterSpacing: "0.1em" }}
        >
          無料相談を申し込む
          <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <>
      <ServicesHero />
      <ServicesList />
      <ServicesCTA />
    </>
  );
}
